import gsap from "gsap";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { shouldBlockMapEntityCardOpen } from "@/features/add-device/should-block-map-entity-card-open";
import { INFO_CARD_TYPE } from "@/features/cards/shared/card-routing/card-types";
import { FunctionalZoneInfoCard } from "@/features/cards/deprecated/info-cards/zone-info-card/functional-zone-info-card";
import { openZustandCard } from "@/features/cards/shared/legacy/open-zustand-card";
import ZoneIcon from "@/icons/inline-react-svgs/zone-icon";
import { useStore } from "@/store/store";
import { hsvToRgb, rgbToHsv } from "../../../utils/color-stuff";
import { getNDVIColorComponents } from "../../../utils/ndvi/get-ndvi-color";
import FocusContext from "../focus-context";
import { addMapEntityPolygonToMap } from "../map-helper";
import MapEntityBase from "./map-entity-base";

const BASE_LINE_WEIGHT = 2;
const BASE_LINE_COLOR = "#82c926";
const HIGHLIGHTED_COLOR = "#26e00c";

export default class ZoneMapEntity extends MapEntityBase {
    static infoCardClass = FunctionalZoneInfoCard;

    infoCardClass = FunctionalZoneInfoCard;

    /**
     *
     * @type {zone}
     */
    model = undefined;

    icon = ZoneIcon;

    /**
     * @param {Zone} zone
     * @param {boolean} renderImmediately
     */
    constructor(zone, renderImmediately = true) {
        super(zone, generateUID("Z"), false);
        this.model = zone;
        if (this.model.vertices && this.model.vertices.length > 0) {
            const maxPoint = [-100000, -100000];
            const minPoint = [100000, 100000];
            this.model.vertices.forEach((vertice) => {
                maxPoint[0] = Math.max(maxPoint[0], vertice.lat);
                maxPoint[1] = Math.max(maxPoint[1], vertice.lng);
                minPoint[0] = Math.min(minPoint[0], vertice.lat);
                minPoint[1] = Math.min(minPoint[1], vertice.lng);
            });
            this.lat = (maxPoint[0] + minPoint[0]) / 2;
            this.long = (maxPoint[1] + minPoint[1]) / 2;
            this.model.onChange.addListener(() => {
                if (this.leafletElement) {
                    this.leafletElement.setLatLngs(this.model.polygon);
                }
            });
        }

        if (renderImmediately) {
            this.renderOnMap();
        }
    }

    renderOnMap() {
        if (!this.renderState.onMap && !this.renderState.beingRendered) {
            this.renderState.beingRendered = true;

            if (this.model?.recentData?.avgNDVI) {
                const zoneColor = getNDVIColorComponents(this.model.recentData.avgNDVI.value);
                let dim = rgbToHsv(...zoneColor);
                dim[2] *= 0.8;
                dim[1] = 1;
                dim = hsvToRgb(...dim);

                let dark = rgbToHsv(...zoneColor);
                dark[2] *= 0.6;
                dark[1] = 1;
                dark = hsvToRgb(...dark);
            }

            addMapEntityPolygonToMap(this, this.model.vertices)
                .then((r) => {
                    this.leafletElement = r.leafletPolygon;
                    this.leafletElement.on("click", (e) => {
                        if (!useStore.getState().isRepositioningDevice) {
                            /**
                             * With persistent cards (scheduler, add-device mid-flow, irrigation report), route
                             * clicks through the focus stack instead of opening a new card.
                             *
                             * To prevent usurping, add a card type to the persistentCardTypes constant instead.
                             *
                             * Set to be removed in this ticket: https://linear.app/verdiag/issue/SWD-2540/unify-card-focus-functionality
                             */
                            if (!shouldBlockMapEntityCardOpen(FocusContext.currentInfoCard?.currentInfoCardType)) {
                                openZustandCard({ entity: this, cardType: INFO_CARD_TYPE.ZONE });
                            } else {
                                FocusContext.onInteraction(e.originalEvent || e, this);
                            }
                        }
                    });
                    this.renderState.onMap = true;
                    this.renderState.beingRendered = false;

                    this.updateFocusState();
                })
                .catch(() => {
                    this.renderState.beingRendered = false;
                    this.renderState.onMap = false;
                });
        }
    }

    updateFocusState() {
        super.updateFocusState();
        this.updateTweener();
    }

    propsOfLeaflet = {
        color: BASE_LINE_COLOR,
        weight: BASE_LINE_WEIGHT,
        opacity: 1,
        fillOpacity: 0.25,
        fillColor: "#4e9800",
        patternOpacity: 0,
    };

    paintTweenUpdate() {
        const propsToSend = { ...this.propsOfLeaflet };
        propsToSend.imgId = propsToSend.patternOpacity > 0.05 ? "lamp" : null;
        if (this.leafletElement) {
            this.leafletElement.setStyle(propsToSend);
        }
    }

    updateTweener() {
        gsap.to(this.propsOfLeaflet, {
            overwrite: "auto",
            duration: 0.4,
            onUpdate: () => {
                this.paintTweenUpdate();
            },
            color: () =>
                ({
                    focused: HIGHLIGHTED_COLOR,
                    selected: HIGHLIGHTED_COLOR,
                    inactive: "#F0FFD3FF",
                })[this.focusState] || BASE_LINE_COLOR,
            weight: () =>
                ({
                    focused: BASE_LINE_WEIGHT + 2,
                    selected: BASE_LINE_WEIGHT + 0.5,
                    inactive: BASE_LINE_WEIGHT - 0.5,
                })[this.focusState] || BASE_LINE_WEIGHT,
            fillOpacity: () =>
                ({
                    focused: 0.35,
                    selected: 0.25,
                    inactive: 0.1,
                })[this.focusState] || 0.25,
            opacity: () =>
                ({
                    focused: 1,
                    selected: 1,
                    inactive: 0.4,
                })[this.focusState] || 1,
            fillColor: () =>
                ({
                    focused: HIGHLIGHTED_COLOR,
                    selected: HIGHLIGHTED_COLOR,
                    inactive: "#F0FFD3FF",
                })[this.focusState] || "#4e9800",
            patternOpacity: () =>
                ({
                    focused: 1,
                    selected: 0,
                    inactive: 0,
                })[this.focusState] || 0,
            ease: "power1.inOut",
        });
    }
}
